import { useCurrentUser } from 'FrontRoyalAngular';
import { parseHtml } from 'FrontRoyalAngular/html.utils';
import { type FC, memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectInGhostMode } from 'Users/ghostModeSlice';
import { userActionsApi } from 'Users';
import { DismissableMessage } from 'DismissableMessage/DismissableMessage';
import clsx from 'clsx';
import { Text } from 'Text';
import { useChatContext } from '../Chat/ChatContext';

const WelcomeModalComponent: FC = () => {
    const { t } = useTranslation('back_royal');
    const { tutorBotStatusConfig, welcomeModalClassname } = useChatContext();
    const { isLocked } = tutorBotStatusConfig;
    const currentUser = useCurrentUser();
    const inGhostMode = useSelector(selectInGhostMode);
    const [updateCurrentUser] = userActionsApi.useUpdateCurrentUserMutation();
    const hasSeenMessage = currentUser?.hasSeenAiWelcomeMessage;

    // In some states we have a CTA that we want to show even if the user has already seen the message.
    // When that happens, we also show the welcomeModalTexts -- even though this user may have already
    // seen and dismissed that message -- because they need to see the CTA.  It is still dismissible
    // within that conversation, and we still set has_seen_ai_welcome_message to true.
    const shouldShowInThisSession = !hasSeenMessage || !!tutorBotStatusConfig.welcomeModalCta;
    const [showUnlockedMessage, setShowUnlockedMessage] = useState(shouldShowInThisSession);

    // When a user dismisses the welcome message, there are a few moments when
    // showUnlockedMessage switches to `false` but `hasSeenMessage` is also still `false`.
    // In that case, we continue to render the jsx as the message transitions off the screen.
    // Once the transition is complete, handleAfterLeave triggers, `hasSeenMessage` switches to `true`,
    // and this component returns null.
    const handleAfterLeave = useCallback(() => {
        if (!currentUser) return;
        if (currentUser.hasSeenAiWelcomeMessage) return;

        if (!inGhostMode) updateCurrentUser({ user: { id: currentUser.id, has_seen_ai_welcome_message: true } });
    }, [currentUser, inGhostMode, updateCurrentUser]);

    // Messages shown in the unlocked state are dismissable. Once one has been dismissed,
    // we don't show anything here.
    if (!isLocked && !shouldShowInThisSession) return null;

    // Some locked statuses do not have a welcome message, only greeting messages. In those
    // cases we don't show anything here.
    if (tutorBotStatusConfig.welcomeModalTexts.length === 0) return null;

    return (
        <div className={clsx('flex', welcomeModalClassname)}>
            <div className="w-[95vw] max-w-[400px]">
                <DismissableMessage
                    disableDismiss={isLocked}
                    show={isLocked || showUnlockedMessage}
                    onDismiss={() => setShowUnlockedMessage(false)}
                    onAfterLeave={handleAfterLeave}
                    cta={
                        tutorBotStatusConfig.welcomeModalCta && {
                            text: {
                                headline: tutorBotStatusConfig.welcomeModalCta.message.headline,
                                subtext: tutorBotStatusConfig.welcomeModalCta.message.subtext,
                            },
                            buttonText: tutorBotStatusConfig.welcomeModalCta.buttonText,
                            onClick: tutorBotStatusConfig.welcomeModalCta.onClick,
                        }
                    }
                >
                    <Text className="text-sm" color="white">
                        {tutorBotStatusConfig.welcomeModalTexts.map(welcomeModalText => (
                            // We need parseHtml here because the welcome_message_notice keys include links
                            <p key={welcomeModalText.key}>
                                {parseHtml(t(welcomeModalText.key, welcomeModalText.params ?? {}))}
                            </p>
                        ))}
                    </Text>
                </DismissableMessage>
            </div>
        </div>
    );
};

export const WelcomeModal = memo(WelcomeModalComponent) as typeof WelcomeModalComponent;
